"use client";

import { ConfirmAction } from "@atlas/composite";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  FormRadioGroup,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import { IHubCatalogVisibility } from "@enums";
import { useForm } from "react-hook-form";

interface ISubmitForReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCancel: () => void;
  onSubmit: (visibility: IHubCatalogVisibility) => void;
  catalogName: string;
}

export function SubmitForReviewDialog({
  open,
  onOpenChange,
  onCancel,
  onSubmit,
  catalogName,
}: ISubmitForReviewDialogProps) {
  const { t } = useTranslation();
  const form = useForm({
    defaultValues: {
      visibility: IHubCatalogVisibility.Private,
    },
  });

  const displayName = catalogName.trim();
  const catalogNameLabel = t("hub.header.catalog", {
    defaultValue: "Catalog",
  });

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen);
    if (!isOpen) {
      onCancel();
    }
  };

  const handleConfirm = () => {
    onOpenChange(false);
    onSubmit(form.getValues("visibility"));
  };

  const options = [
    {
      label: "Private",
      value: IHubCatalogVisibility.Private,
      description: t("hub.submit_for_review.visibility.private.description", {
        defaultValue:
          "This catalog is available for you only and the review is not required.",
      }),
    },
    {
      label: "Company",
      value: IHubCatalogVisibility.Company,
      description: t("hub.submit_for_review.visibility.company.description", {
        defaultValue:
          "Available to anyone inside company and review is not required.",
      }),
    },
    {
      label: "Platform",
      value: IHubCatalogVisibility.Platform,
      description: t("hub.submit_for_review.visibility.platform.description", {
        defaultValue:
          "Available to Iron Mountain / partner platforms. Review is required.",
      }),
    } /* 
    {
      label: "Global",
      value: IHubCatalogVisibility.Global,
      description: t("hub.submit_for_review.visibility.global.description", {
        defaultValue:
          "Available globally across all platforms. Review is required.",
      }),
    }, */,
  ];

  return (
    <ConfirmAction
      open={open}
      onOpenChange={handleOpenChange}
      title={t("hub.submit_for_review.title", {
        defaultValue: "Submit",
      })}
      description={
        <div className="space-y-4 mt-4">
          <Alert variant="default">
            <AlertTitle>{catalogNameLabel}</AlertTitle>
            <AlertDescription>
              {displayName ||
                t("hub.submit_for_review.catalog_name_empty", {
                  defaultValue: "—",
                })}
            </AlertDescription>
          </Alert>

          <div className="space-y-3 mt-4">
            <FormRadioGroup
              control={form.control}
              name="visibility"
              options={options}
            />
          </div>
        </div>
      }
      cancelText={t("hub.administer.cancel", { defaultValue: "Cancel" })}
      confirmText={t("hub.create_catalog.submit", { defaultValue: "Submit" })}
      onConfirm={handleConfirm}
    />
  );
}
